import type { DataSourceParameterType } from '@/types'
import {
  inferDependsOnFromOptionsSql,
  isDropdownParameterType,
  type DataSourceInputParam,
} from '@/lib/sqlPlaceholders'
import { partitionInjectedParameters } from '@/lib/injectedParameters'
import { SnippetPicker } from '@/components/SnippetPicker'
import { cn } from '@/lib/utils'

const PARAMETER_TYPES: DataSourceParameterType[] = [
  'Dropdown',
  'MultiSelect',
  'Calendar',
  'DateRange',
  'Text',
]

type Props = {
  params: DataSourceInputParam[]
  onChange: (next: DataSourceInputParam[]) => void
  className?: string
}

export function DataSourceParameterToggles({ params, onChange, className }: Props) {
  const { visible, injected } = partitionInjectedParameters(params)

  if (params.length === 0) return null

  function commitVisible(visibleNext: DataSourceInputParam[]) {
    onChange([...injected, ...visibleNext])
  }

  function updateParam(name: string, patch: Partial<DataSourceInputParam>) {
    commitVisible(visible.map(p => (p.name === name ? { ...p, ...patch } : p)))
  }

  function toggleRequired(name: string) {
    commitVisible(visible.map(p => (p.name === name ? { ...p, isRequired: !p.isRequired } : p)))
  }

  function setOptionsSql(name: string, optionsSql: string) {
    const inferred = inferDependsOnFromOptionsSql(optionsSql, name)
    updateParam(name, { optionsSql, dependsOn: inferred })
  }

  const otherNames = (exclude: string) =>
    visible.map(p => p.name).filter(n => n !== exclude)

  return (
    <div className={cn('space-y-4', className)}>
      {injected.length > 0 ? (
        <p className="text-[10px] text-muted font-mono">
          <code className="text-ink/80">userId</code> is supplied automatically from the signed-in user.
        </p>
      ) : null}

      {visible.length === 0 ? null : (
        <>
          <div className="hidden min-w-0 text-[10px] font-semibold uppercase tracking-wide text-muted sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-2">
            <span>Placeholder</span>
            <span className="text-right">Requirement</span>
          </div>
          {visible.map(p => (
            <div
              key={p.name}
              className="space-y-2 rounded-md border border-border2 bg-bg/40 p-3"
            >
              <label className="block min-w-0">
                <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted">Caption</span>
                <input
                  type="text"
                  className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs text-ink placeholder:text-muted focus:border-accent focus:outline-none"
                  placeholder="Label shown in report parameters"
                  value={p.caption}
                  onChange={e => updateParam(p.name, { caption: e.target.value })}
                />
              </label>
              <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                <code className="truncate rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-green">
                  {'{{'}
                  {p.name}
                  {'}}'}
                </code>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-[10px] font-mono text-muted">{p.isRequired ? 'Required' : 'Optional'}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={p.isRequired}
                    onClick={() => toggleRequired(p.name)}
                    className={cn(
                      'relative h-5 w-10 flex-shrink-0 rounded-full transition-colors',
                      p.isRequired ? 'bg-accent' : 'bg-border2'
                    )}
                  >
                    <span
                      className={cn(
                        'pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                        p.isRequired ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </button>
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted">Type</span>
                  <select
                    className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink"
                    value={p.parameterType ?? 'Dropdown'}
                    onChange={e => {
                      const parameterType = e.target.value as DataSourceParameterType
                      if (isDropdownParameterType(parameterType)) {
                        const sql = (p.optionsSql ?? '').trim()
                        updateParam(p.name, {
                          parameterType,
                          optionsSql: sql || p.optionsSql,
                          dependsOn: sql ? inferDependsOnFromOptionsSql(sql, p.name) : (p.dependsOn ?? []),
                        })
                      } else {
                        updateParam(p.name, { parameterType, optionsSql: null, dependsOn: [] })
                      }
                    }}
                  >
                    {PARAMETER_TYPES.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>
                {isDropdownParameterType(p.parameterType) ? (
                  <div className="min-w-0 text-[10px] text-muted sm:col-span-2">
                    <span className="font-semibold text-ink/80">Depends on:</span>{' '}
                    {(p.dependsOn ?? []).length > 0 ? (p.dependsOn ?? []).join(', ') : '—'}
                    <span className="block mt-0.5">Derived from {'{{placeholders}}'} in options SQL (must match API).</span>
                  </div>
                ) : null}
              </div>

              {isDropdownParameterType(p.parameterType) ? (
                <label className="block min-w-0">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="block text-[10px] font-semibold uppercase tracking-wide text-muted">
                      Options SQL (SELECT … id, name …)
                    </span>
                    <SnippetPicker
                      type="InputParameter"
                      compact
                      className="flex-shrink-0"
                      onInsert={text => setOptionsSql(p.name, text)}
                    />
                  </div>
                  <textarea
                    className="h-24 w-full resize-y rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none"
                    placeholder={'SELECT roomId AS id, roomName AS name FROM rooms WHERE sectorId IN ({{sectorId}})'}
                    value={p.optionsSql ?? ''}
                    onChange={e => setOptionsSql(p.name, e.target.value)}
                  />
                </label>
              ) : null}

              <p className="text-[10px] text-muted">
                Other placeholders: {otherNames(p.name).join(', ') || '—'}
              </p>
            </div>
          ))}
          <p className="text-[10px] text-muted">
            Tokens match <code className="font-mono text-ink/80">{'{{Name}}'}</code> in the SQL. Toggle whether each must be supplied at runtime.
          </p>
        </>
      )}
    </div>
  )
}
